import type { FieldSelection, RelationQuery } from '#layers/data-model/types/field-selection';
import type { GlobalCollectionId } from '#layers/data-model/types/schema/ids';
import { getPrimitiveFieldSelection, isRelationQuery } from '#layers/data-model/types/field-selection';

export interface ExpandFieldSelectionOptions {
	collectionId: GlobalCollectionId;
	selection: FieldSelection;
	resolveField: (collectionId: GlobalCollectionId, apiName: string) => {
		isRelation: boolean;
		oppositeCollectionId?: GlobalCollectionId;
	} | undefined;
	getCollectionKeyFieldNames: (collectionId: GlobalCollectionId) => string[];
	getRelationJoinFields: (collectionId: GlobalCollectionId, relationApiName: string) => {
		ownFields: string[];
		oppositeFields: string[];
	};
}

export function expandRequiredFields(options: ExpandFieldSelectionOptions): FieldSelection {
	return expandAtLevel(options.collectionId, options.selection, options);
}

function expandAtLevel(
	collectionId: GlobalCollectionId,
	selection: FieldSelection,
	options: ExpandFieldSelectionOptions,
): FieldSelection {
	const primitives: string[] = [];
	const resultRelations: FieldSelection = [];
	const ownFks = new Set<string>();
	let hasWildcard = false;

	for (const entry of selection) {
		if (typeof entry === 'string') {
			primitives.push(entry);
			if (entry === '*') hasWildcard = true;
			continue;
		}

		for (const [key, value] of Object.entries(entry)) {
			const field = options.resolveField(collectionId, key);

			if (!field?.isRelation || !field.oppositeCollectionId) {
				resultRelations.push({ [key]: value });
				continue;
			}

			const isQuery = isRelationQuery(value);
			const nestedFields = isQuery ? (value as RelationQuery).fields : (value as FieldSelection);

			let expandedFields = expandAtLevel(field.oppositeCollectionId, nestedFields, options);

			const joinFields = options.getRelationJoinFields(collectionId, key);

			if (!getPrimitiveFieldSelection(expandedFields).includes('*')) {
				const pks = options.getCollectionKeyFieldNames(field.oppositeCollectionId);
				expandedFields = injectPrimitives(expandedFields, [...pks, ...joinFields.oppositeFields]);
			}

			if (isQuery) {
				const { fields: _, ...queryOpts } = value as RelationQuery;
				resultRelations.push({ [key]: { fields: expandedFields, ...queryOpts } });
			}
			else {
				resultRelations.push({ [key]: expandedFields });
			}

			for (const fk of joinFields.ownFields) {
				ownFks.add(fk);
			}
		}
	}

	let resultPrimitives = [...primitives];

	if (!hasWildcard) {
		resultPrimitives = [...new Set([
			...resultPrimitives,
			...options.getCollectionKeyFieldNames(collectionId),
			...ownFks,
		])];
	}

	return [...resultPrimitives, ...resultRelations];
}

function injectPrimitives(selection: FieldSelection, fields: string[]): FieldSelection {
	if (fields.length === 0 || getPrimitiveFieldSelection(selection).includes('*')) return selection;
	const primitives = [...new Set([...getPrimitiveFieldSelection(selection), ...fields])];
	return [...primitives, ...selection.filter((e) => typeof e !== 'string')];
}
